import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { DE_PDF_TOOLS, DE_PDF_CATEGORIES, type DePdfCategory } from "@/lib/de-pdf-catalog";
import { FileText, ShieldCheck, Search, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/de/pdf-tools/")({
  head: () => ({
    meta: [
      { title: "Kostenlose PDF Werkzeuge Online — PDF Toolkit Pro | Picamini.online" },
      {
        name: "description",
        content:
          "26+ kostenlose Online PDF-Tools: PDF zusammenfügen, teilen, komprimieren, bearbeiten, in Word/Excel umwandeln & schützen. 100% privat im Browser ohne Upload.",
      },
      {
        name: "keywords",
        content:
          "pdf werkzeuge kostenlos, pdf zusammenfügen, pdf teilen, pdf komprimieren, pdf bearbeiten online, jpg in pdf umwandeln, pdf in word kostenlos, pdf schützen",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Kostenlose PDF Werkzeuge Online — 100% Privat im Browser" },
      {
        property: "og:description",
        content: "Bearbeiten Sie Ihre PDF-Dateien superschnell und datenschutzkonform ohne Upload auf externe Server.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/de/pdf-tools" },
      { property: "og:locale", content: "de_DE" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/de/pdf-tools" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/pdf-tools" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/pdf-tools" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/pdf-tools" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/pdf-tools" },
    ],
  }),
  component: DePdfHubPage,
});

function DePdfHubPage() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<DePdfCategory | "Alle">("Alle");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DE_PDF_TOOLS.filter((t) => {
      const matchesCategory = category === "Alle" || t.category === category;
      const matchesQuery =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.tagline.toLowerCase().includes(q) ||
        t.keywords.some((k) => k.toLowerCase().includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <SiteHeader lang="de" />

      <main className="flex-1">
        {/* HERO */}
        <section className="bg-gradient-to-b from-primary/5 via-background to-background py-16 sm:py-20 border-b border-border/50">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 text-center space-y-4">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary">
              <ShieldCheck className="size-4" /> PDF Toolkit Pro — 100% Client-Side Verarbeitung
            </span>

            <h1 className="text-3xl font-extrabold tracking-tight sm:text-5xl text-foreground max-w-4xl mx-auto">
              Kostenlose PDF-Werkzeuge Online & Privat
            </h1>

            <p className="max-w-3xl mx-auto text-base sm:text-lg text-muted-foreground leading-relaxed">
              Fügen Sie PDFs zusammen, teilen, komprimieren, bearbeiten und konvertieren Sie Dokumente direkt im Browser. Ihre Dateien verlassen niemals Ihren Computer und werden auf keinen Server hochgeladen.
            </p>

            {/* SEARCH BAR */}
            <div className="mx-auto mt-6 flex max-w-lg items-center gap-2 rounded-2xl bg-card p-2 shadow-md border border-border">
              <Search className="size-5 ml-2 text-muted-foreground shrink-0" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="PDF-Werkzeug suchen (z. B. zusammenfügen, komprimieren, schützen)…"
                aria-label="PDF-Werkzeug suchen"
                className="border-0 bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:ring-0 text-sm sm:text-base font-medium"
              />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 pt-4 text-xs sm:text-sm font-semibold text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-emerald-500" /> Ohne Dateilimit
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-emerald-500" /> Ohne Registrierung
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-emerald-500" /> 100% Datenschutz
              </span>
            </div>
          </div>
        </section>

        {/* CATEGORIES FILTER & TOOLS GRID */}
        <section className="py-12 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            <Button
              size="sm"
              variant={category === "Alle" ? "default" : "outline"}
              onClick={() => setCategory("Alle")}
              className="rounded-xl font-bold"
            >
              Alle PDF-Werkzeuge ({DE_PDF_TOOLS.length})
            </Button>
            {DE_PDF_CATEGORIES.map((cat) => (
              <Button
                key={cat}
                size="sm"
                variant={category === cat ? "default" : "outline"}
                onClick={() => setCategory(cat)}
                className="rounded-xl font-semibold"
              >
                {cat}
              </Button>
            ))}
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((tool) => (
              <Link
                key={tool.slug}
                to="/de/pdf-tools/$slug"
                params={{ slug: tool.slug }}
                className="surface-card p-6 rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-md flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
                      <FileText className="size-5" />
                    </span>
                    <h3 className="font-bold text-base text-foreground">{tool.name}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tool.description}</p>
                </div>
                <div className="pt-4 flex items-center gap-1.5 text-xs font-bold text-primary">
                  Werkzeug öffnen <ArrowRight className="size-3.5" />
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center space-y-3">
              <p className="text-lg font-semibold text-muted-foreground">
                Keine PDF-Werkzeuge gefunden für "{query}".
              </p>
              <Button variant="outline" onClick={() => { setQuery(""); setCategory("Alle"); }}>
                Suchfilter zurücksetzen
              </Button>
            </div>
          )}
        </section>
      </main>

      <SiteFooter lang="de" />
    </div>
  );
}

